import { inject, observer } from 'mobx-react';
import React from 'react';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import { RouteComponentProps } from 'react-router';
import { Instance } from 'mobx-state-tree';
import { AuthenticationStore, SecretariatStore, SystemStatusStore, ApplicationStore } from '../../store';
import { SystemStatusForm } from './SystemStatusForm';
import { SystemStatus } from '../../store/models';
import MainSectionLayout from '../layout/MainSectionLayout';
import styled from '@emotion/styled';
import { css } from 'emotion';
import { Card } from '@blueprintjs/core';
import { Page, TLCode } from '../../common/enums';

const Container = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
});

const CardStyle = css({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

interface SystemStatusPageProps extends RouteComponentProps<{ id: string }> {
  SystemStatusStore?: Instance<typeof SystemStatusStore>;
  SecretariatStore?: Instance<typeof SecretariatStore>;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
  ApplicationStore?: Instance<typeof ApplicationStore>;
}

interface SystemStatusPageState {
  isLoading: boolean;
  systemStatus?: Instance<typeof SystemStatus>;
}

@inject('SystemStatusStore', 'SecretariatStore', 'AuthenticationStore', 'ApplicationStore')
@observer
class SystemStatusPage extends React.Component<SystemStatusPageProps & WithTranslateProps, SystemStatusPageState> {
  state = {
    isLoading: false,
  } as SystemStatusPageState;

  _isMounted = false;

  async fetchData() {
    const { SystemStatusStore, SecretariatStore } = this.props;
    const systemStatusId = parseInt(this.props.match.params.id, 10);
    this._isMounted && this.setState({ isLoading: true });
    SecretariatStore!.checkListLoaded();
    if (systemStatusId > 0) {
      await SystemStatusStore!.loadList();
      const systemStatus = SystemStatusStore!.getById(systemStatusId);
      this._isMounted && this.setState({ systemStatus });
    }
    this._isMounted && this.setState({ isLoading: false });
  }

  async componentDidMount() {
    this._isMounted = true;
    await this.fetchData();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  componentDidUpdate(prevProps: SystemStatusPageProps, prevState: SystemStatusPageState) {
    if (prevProps.match.params.id !== this.props.match.params.id) {
      this.fetchData();
    }
  }

  backToSecretariatList = () => {
    if (this.state.systemStatus)
      this.props.ApplicationStore!.navigate(
        `${Page.SecretariatPage}/${this.state.systemStatus!.secretariatId}?from=systemStatusForm`,
      );
  };

  renderTitle = () => {
    const systemStatus = this.state.systemStatus;
    return systemStatus !== undefined ? (
      <h1>
        {this.props.translate(TLCode.SYSTEM_STATUS_Edit)}&nbsp;
        {systemStatus!.subSystem + ' (id: ' + systemStatus.systemStatusId + ')'}
      </h1>
    ) : (
      <h1>{this.props.translate(TLCode.SYSTEM_STATUS_Add)}</h1>
    );
  };

  saveSystemStatus = async (systemStatus?: Instance<typeof SystemStatus>) => {
    this._isMounted && this.setState({ isLoading: true });
    systemStatus && (await this.props.SystemStatusStore!.saveSystemStatus(systemStatus!));
    this._isMounted && this.setState({ isLoading: false });
  };

  render() {
    const { systemStatus, isLoading } = this.state;
    const { SecretariatStore, AuthenticationStore, translate } = this.props;
    return (
      <MainSectionLayout isLoading={isLoading} renderHeader={this.renderTitle}>
        <Container>
          <Card className={CardStyle.toString()}>
            <SystemStatusForm
              systemStatus={systemStatus}
              secretariats={SecretariatStore!.dataList}
              save={this.saveSystemStatus}
              isSuperAdmin={AuthenticationStore!.isSuperAdmin}
              translate={translate}
              backToSecretariatList={this.backToSecretariatList}
            />
          </Card>
        </Container>
      </MainSectionLayout>
    );
  }
}

export default withTranslate(SystemStatusPage);
